import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createCleanerRequest,
  deleteCleanerRequest,
  getCleanersRequest,
  updateCleanerRequest,
} from "../../api/cleanerApi";
import { AuthUserResponse } from "../../models/ResponseModels/AuthUserResponse";
import { CreateCleanerRequest } from "../../models/RequestModels/CreateCleanerRequest";
import { UpdateCleanerRequest } from "../../models/RequestModels/UpdateCleanerRequest";
import {
  getApiErrorMessage,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface CleanerState {
  cleaners: AuthUserResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: CleanerState = {
  cleaners: [],
  loading: false,
  error: null,
};

export const fetchCleaners = createAsyncThunk(
  "cleaners/fetchCleaners",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCleanersRequest();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createCleaner = createAsyncThunk(
  "cleaners/createCleaner",
  async (body: CreateCleanerRequest, { rejectWithValue }) => {
    try {
      const response = await createCleanerRequest(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateCleaner = createAsyncThunk(
  "cleaners/updateCleaner",
  async (
    payload: { id: number; body: UpdateCleanerRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateCleanerRequest(payload.id, payload.body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteCleaner = createAsyncThunk(
  "cleaners/deleteCleaner",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteCleanerRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const cleanerSlice = createSlice({
  name: "cleaners",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCleaners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCleaners.fulfilled, (state, action) => {
        state.loading = false;
        state.cleaners = action.payload;
      })
      .addCase(fetchCleaners.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to load cleaners.",
        );
      })

      .addCase(createCleaner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCleaner.fulfilled, (state, action) => {
        state.loading = false;
        state.cleaners.unshift(action.payload);
      })
      .addCase(createCleaner.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to create cleaner.",
        );
      })

      .addCase(updateCleaner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCleaner.fulfilled, (state, action) => {
        state.loading = false;
        state.cleaners = state.cleaners.map((cleaner) =>
          cleaner.id === action.payload.id ? action.payload : cleaner,
        );
      })
      .addCase(updateCleaner.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to update cleaner.",
        );
      })

      .addCase(deleteCleaner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCleaner.fulfilled, (state, action) => {
        state.loading = false;
        state.cleaners = state.cleaners.filter(
          (cleaner) => cleaner.id !== action.payload,
        );
      })
      .addCase(deleteCleaner.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to delete cleaner.",
        );
      });
  },
});

export default cleanerSlice.reducer;
